import React, { FC, useState, useRef } from 'react';
import Subheading from '../font/Subheading';
import styled from 'styled-components';
import { TwitterPicker } from 'react-color';
import { Switch } from 'antd';

import Spacer from '../../components/layout/Spacer';
import { useAppSelector, useAppDispatch } from '../../redux/redux.hooks';
import RadioButton from '../../components/radio/RadioButton';
import { hangerIcons } from '../../utils/hanger.icons';
import { shoeIcons } from '../../utils/shoe.icons';
import Dropdown from '../../components/dropdown/Dropdown';
import DropdownItem from '../../components/dropdown/DropdownItem';
import {
  changeDesktopBackgroundColor,
  changeDesktopBorderColor,
  changeDesktopBorderShape,
  changeDesktopBorderType,
  changeDesktopFontSize,
  changeDesktopFontWeight,
  changeDesktopJustifyContent,
  changeDesktopMargin,
  changeDesktopOnlyCapitalLettersStatus,
  changeDesktopPadding,
  changeDesktopUnderlineStatus,
  changeDesktopWidth,
  changeMobileBackgroundColor,
  changeMobileBorderColor,
  changeMobileBorderShape,
  changeMobileBorderType,
  changeMobileFontSize,
  changeMobileFontWeight,
  changeMobileJustifyContent,
  changeMobileMargin,
  changeMobileOnlyCapitalLettersStatus,
  changeMobilePadding,
  changeMobileUnderlineStatus,
  changeMobileWidth,
  changeShoeIcon,
  changeFontColor,
  changeHangerIcon,
  changeIconType,
  setButtonCss,
  setCustomIconUrl,
  resetData,
  changeDesktopBorderRadius,
  changeMobileBorderRadius,
  changeSpecialIcon,
  changeDesktopHoverBackgroundColor,
  changeDesktopHoverStyle,
  changeMobileHoverBackgroundColor,
  changeMobileHoverStyle,
  changeDesktopButtonPosition,
  changeMobileButtonPosition,
  changeSwimwearIcon,
  changeApparelIcon,
  changeBottomIcon,
  changeJacketIcon,
} from '../../reducers/fit.quiz.button.reducer';
import Row from '../../components/layout/Row';
import ColumnItem from '../../components/layout/ColumnItem';
import RadioButtonContent from '../../components/radio/RadioButtonContent';
import RadioButtonWrapper from '../radio/RadioButtonWrapper';
import { generateCssFromButtonState } from '../../utils/button.css.helper';
import { ShopFeature } from '../../types/fitQuizButton/ShopFeature';
import { P } from '../font/Parargraph';
import { useClickOutside } from '../../hooks/use.click.outside';
import { Info } from '../font/Info';
import FitquizAdvancedSettings from './FitquizAdvancedSettings';
import { Text } from '../font/Text';
import { specialIcons } from '../../utils/special.icons';
import { swimwearIcons } from '../../utils/swimwear.icons';
import { bottomsIcons } from '../../utils/bottoms.icons';
import { apparelIcons } from '../../utils/apparel.icons';
import { jacketIcons } from '../../utils/jacket.icons';

type FitQuizSettingsProps = {
  shop_id?: string;
};

const FitQuizSettings: FC<FitQuizSettingsProps> = ({ shop_id }) => {
  const dispatch = useAppDispatch();
  const { data, status } = useAppSelector((state) => state.fitButtonReducer);
  const hangerRef = useRef<HTMLDivElement>(null);
  const shoeRef = useRef<HTMLDivElement>(null);
  const specialIconRef = useRef<HTMLDivElement>(null);
  const apparelIconRef = useRef<HTMLDivElement>(null);
  const bottomIconRef = useRef<HTMLDivElement>(null);
  const jacketIconRef = useRef<HTMLDivElement>(null);
  const swimwearIconRef = useRef<HTMLDivElement>(null);
  const textColorRef = useRef<HTMLDivElement>(null);

  const [desktopAndMobileSimilar, setDesktopAndMobileSimilar] = useState(true);
  const [showHangerDropdown, setShowHangerDropdown] = useState<boolean>(false);
  const [showShoeDropdown, setShowShoeDropdown] = useState<boolean>(false);
  const [showSpecialIconDropdown, setShowSpecialIconDropdown] =
    useState<boolean>(false);
  const [showSwimwearIconDropdown, setShowSwimwearIconDropdown] = useState<boolean>(false);
  const [showBottomIconDropdown, setShowBottomIconDropdown] = useState<boolean>(false);
  const [showApparelIconDropdown, setShowApparelIconDropdown] = useState<boolean>(false);
  const [showJacketIconDropdown, setShowJacketIconDropdown] = useState<boolean>(false);
  const [showCustomIconTextfield, setShowCustomIconTextfield] =
    useState<boolean>(false);
  const [showFontColorPicker, setShowFontColorPicker] =
    useState<boolean>(false);

  useClickOutside(hangerRef, () => setShowHangerDropdown(false));
  useClickOutside(shoeRef, () => setShowShoeDropdown(false));

  useClickOutside(textColorRef, () => setShowFontColorPicker(false));

  const {
    desktopAndButtonStyleSame,
    fontColor,
    iconType,
    specialIcon,
    hangerIcon,
    swimwearIcon,
    bottomsIcon,
    jacketIcon,
    apparelIcon,
    shoeIcon,
    customIconUrl,
    desktopAdvancedSettings,
    mobileAdvancedSettings,
  } = data;

  /* --------------- ICON HANDLERS ----------------- */

  const __closeAllIconDropdowns = () => {
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }

    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    } 
  };

  const handleSpecialIconRadioClick = () => {
    dispatch(changeIconType('Special'));
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    }
    setShowSpecialIconDropdown(!showSpecialIconDropdown);
  };

  const handleHangerRadioClick = () => {
    dispatch(changeIconType('Hanger'));
    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    }
    setShowHangerDropdown(!showHangerDropdown);
  };

  const handleShoeRadioClick = () => {
    dispatch(changeIconType('Shoe'));
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    }
    setShowShoeDropdown(!showShoeDropdown);
  };

  const handleBottomRadioClick = () => {
    dispatch(changeIconType('Bottom'));
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    }
    setShowBottomIconDropdown(!showBottomIconDropdown);
  };

  const handleApparelRadioClick = () => {
    dispatch(changeIconType('Apparel'));
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    }
    setShowApparelIconDropdown(!showApparelIconDropdown);
  };

  const handleJacketRadioClick = () => {
    dispatch(changeIconType('Jacket'));
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showSwimwearIconDropdown) {
      setShowSwimwearIconDropdown(false);
    }
    setShowJacketIconDropdown(!showJacketIconDropdown);
  };

  const handleSwimwearRadioClick = () => {
    dispatch(changeIconType('Swimwear'));
    if (showHangerDropdown) {
      setShowHangerDropdown(false);
    }
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    if (showSpecialIconDropdown) {
      setShowSpecialIconDropdown(false);
    }
    if (showShoeDropdown) {
      setShowShoeDropdown(false);
    }
    if (showApparelIconDropdown) {
      setShowApparelIconDropdown(false);
    }
    if (showBottomIconDropdown) {
      setShowBottomIconDropdown(false);
    }
    if (showJacketIconDropdown) {
      setShowJacketIconDropdown(false);
    }
    setShowSwimwearIconDropdown(!showSwimwearIconDropdown);
  };


  const handleNoIconClick = () => {
    dispatch(changeIconType('None'));
    if (showCustomIconTextfield) {
      setShowCustomIconTextfield(false);
    }
    __closeAllIconDropdowns();
  };

  const handleCustomIconClick = () => {
    dispatch(changeIconType('Custom'));
    __closeAllIconDropdowns();
    setShowCustomIconTextfield(!showCustomIconTextfield);
  };

  /* --------------- ICON HANDLERS END ----------------- */

  const submitForm = () => {
    const css = generateCssFromButtonState({
      desktopAndButtonStyleSame,
      fontColor,
      iconType,
      shoeIcon,
      apparelIcon,
      bottomsIcon,
      jacketIcon,
      swimwearIcon,
      specialIcon,
      hangerIcon,
      customIconUrl,
      desktopAdvancedSettings,
      mobileAdvancedSettings,
    });
    const feature: ShopFeature = { shop_id, value: css };
    dispatch(setButtonCss(feature));
  };

  const renderSpecialIcons = () => {
    return (
      <Dropdown ref={specialIconRef}>
        {specialIcons.map((icon) => (
          <DropdownItem
            key={icon.key}
            onClick={() => dispatch(changeSpecialIcon(icon))}
          >
            <img src={icon.url} width={24} height={24} alt={icon.key} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  };

  const renderApparelIcons = () => {
    return (
      <Dropdown ref={apparelIconRef}>
        {apparelIcons.map((icon) => (
          <DropdownItem
            key={icon.key}
            onClick={() => dispatch(changeApparelIcon(icon))}
          >
            <img src={icon.url} width={24} height={24} alt={icon.key} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  };

  const renderBottomIcons = () => {
    return (
      <Dropdown ref={bottomIconRef}>
        {bottomsIcons.map((icon) => (
          <DropdownItem
            key={icon.key}
            onClick={() => dispatch(changeBottomIcon(icon))}
          >
            <img src={icon.url} width={24} height={24} alt={icon.key} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  };

  const renderJacketIcons = () => {
    return (
      <Dropdown ref={jacketIconRef}>
        {jacketIcons.map((icon) => (
          <DropdownItem
            key={icon.key}
            onClick={() => dispatch(changeJacketIcon(icon))}
          >
            <img src={icon.url} width={24} height={24} alt={icon.key} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  };

  const renderSwimwearIcons = () => {
    return (
      <Dropdown ref={swimwearIconRef}>
        {swimwearIcons.map((icon) => (
          <DropdownItem
            key={icon.key}
            onClick={() => dispatch(changeSwimwearIcon(icon))}
          >
            <img src={icon.url} width={24} height={24} alt={icon.key} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  };

  const renderHangerIcons = () => (
    <Dropdown ref={hangerRef}>
      {hangerIcons.map((icon) => (
        <DropdownItem
          key={icon.key}
          onClick={() => dispatch(changeHangerIcon(icon))}
        >
          <img src={icon.url} width={24} height={24} alt={icon.key} />
        </DropdownItem>
      ))}
    </Dropdown>
  );

  const renderShoeIcons = () => (
    <Dropdown ref={shoeRef}>
      {shoeIcons.map((icon) => (
        <DropdownItem
          key={icon.key}
          onClick={() => dispatch(changeShoeIcon(icon))}
        >
          <img src={icon.url} width={24} height={24} alt={icon.key} />
        </DropdownItem>
      ))}
    </Dropdown>
  );

  return (
    <>
      {status === 'successful' && (
        <Container>
          <Header>
            <ColumnItem>
              <s-heading fontSize='large'>FIT quiz button</s-heading>
              <Spacer height={24} />
              <P>
                You can customize the apperance of the button.
                <br />
                Choose the font, color and call to action that works best for
                your store
              </P>
            </ColumnItem>
            <ColumnItem>
              <s-section>
                <Subheading>Boost your growth!</Subheading>
                <Spacer height={8} />
                <P>
                  We advice you to split testing the button to increase the
                  conversion rate for using FIT Quiz and increase CR of a
                  purchase, increase your revenue and reduce returns
                </P>
                <s-stack direction='inline' justifyContent='end'>
                  <s-button variant='tertiary'>Learn more →</s-button>
                </s-stack>
              </s-section>
            </ColumnItem>
          </Header>
          <SettingsSection backgroundColor='#fafafa'>
            <Row>
              <ColumnItem>
                <Subheading>BUTTON CTA</Subheading>
                <Info>
                  This is just a placeholder.
                  <br />
                  To change the actual text, please use the translations tab
                </Info>
                <Spacer height={12} />
                <s-text-field
                  label='Button CTA'
                  labelAccessibilityVisibility='exclusive'
                  value='Not sure about the size'
                  disabled
                  autocomplete='off'
                />
              </ColumnItem>

              <ColumnItem>
                <Subheading>BUTTON ICON</Subheading>
                <Spacer height={8} />
                <Row height={64}>
                <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={specialIcon}
                      onClick={handleSpecialIconRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={specialIcon.url}
                            width={24}
                            height={24}
                            alt={specialIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showSpecialIconDropdown && renderSpecialIcons()}
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={jacketIcon}
                      onClick={handleJacketRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={jacketIcon.url}
                            width={24}
                            height={24}
                            alt={jacketIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showJacketIconDropdown && renderJacketIcons()}
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={bottomsIcon}
                      onClick={handleBottomRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={bottomsIcon.url}
                            width={24}
                            height={24}
                            alt={bottomsIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showBottomIconDropdown && renderBottomIcons()}
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={swimwearIcon}
                      onClick={handleSwimwearRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={swimwearIcon.url}
                            width={24}
                            height={24}
                            alt={swimwearIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showSwimwearIconDropdown && renderSwimwearIcons()}
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={apparelIcon}
                      onClick={handleApparelRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={apparelIcon.url}
                            width={24}
                            height={24}
                            alt={apparelIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showApparelIconDropdown && renderApparelIcons()}
                  </RadioButtonWrapper>
                  
                </Row>
                <Row>
                <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={hangerIcon}
                      onClick={handleHangerRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={hangerIcon.url}
                            width={24}
                            height={24}
                            alt={hangerIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showHangerDropdown && renderHangerIcons()}
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='icons'
                      value={shoeIcon}
                      onClick={handleShoeRadioClick}
                      content={() => (
                        <RadioButtonContent>
                          <img
                            src={shoeIcon.url}
                            width={24}
                            height={24}
                            alt={shoeIcon.key}
                          />
                        </RadioButtonContent>
                      )}
                    />
                    {showShoeDropdown && renderShoeIcons()}
                  </RadioButtonWrapper>

                  <RadioButtonWrapper>
                    <s-button onClick={handleNoIconClick}>No icon</s-button>
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <s-button onClick={handleCustomIconClick}>
                      Choose your own
                    </s-button>
                  </RadioButtonWrapper>
                </Row>
              </ColumnItem>
            </Row>
            <Row>
              <ColumnItem>
                <Subheading>BUTTON COLOR</Subheading>
                <Spacer height={4} />
                <Row>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='color'
                      value='black'
                      checked={fontColor === '#000000'}
                      onClick={() => dispatch(changeFontColor('#000000'))}
                      content={() => (
                        <RadioButtonContent customBackground='#000000' />
                      )}
                    />
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='color'
                      value='blue'
                      checked={fontColor === '#5673C0'}
                      onClick={() => dispatch(changeFontColor('#5673C0'))}
                      content={() => (
                        <RadioButtonContent customBackground='#5673C0' />
                      )}
                    />
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <RadioButton
                      groupName='color'
                      value='red'
                      checked={fontColor === '#C45E5C'}
                      onClick={() => dispatch(changeFontColor('#C45E5C'))}
                      content={() => (
                        <RadioButtonContent customBackground='#C45E5C' />
                      )}
                    />
                  </RadioButtonWrapper>
                  <RadioButtonWrapper>
                    <s-button
                      onClick={() =>
                        setShowFontColorPicker(!showFontColorPicker)
                      }
                    >
                      Choose your own
                    </s-button>
                  </RadioButtonWrapper>
                </Row>
                {showFontColorPicker && (
                  <>
                    <Spacer height={12} />
                    <Row ref={textColorRef}>
                      <TwitterPicker
                        triangle='top-right'
                        color={fontColor}
                        onChangeComplete={(value) =>
                          dispatch(changeFontColor(value.hex))
                        }
                      />
                    </Row>
                  </>
                )}
              </ColumnItem>

              {showCustomIconTextfield && (
                <ColumnItem>
                  <s-text-field
                    label='CUSTOM ICON'
                    onInput={({ currentTarget: { value } }) => dispatch(setCustomIconUrl(value))}
                    placeholder='Please enter the icon url.'
                    value={customIconUrl}
                    autocomplete='off'
                  />
                </ColumnItem>
              )}
            </Row>
          </SettingsSection>
          <SettingsSection backgroundColor='transparent'>
            <ButtonContainer>
              <Switch
                defaultChecked
                style={{
                  backgroundColor: desktopAndMobileSimilar ? '#008060' : '#ccc',
                  alignSelf: 'flex-end',
                }}
                onChange={() =>
                  setDesktopAndMobileSimilar(!desktopAndMobileSimilar)
                }
              />
              &nbsp;
              <Text>Similar settings for desktop and mobile</Text>
            </ButtonContainer>
          </SettingsSection>
          <SettingsSection backgroundColor='#f0f2f5'>
            <FitquizAdvancedSettings
              deviceType='desktop'
              handleFontWeightChanged={(weight) => {
                dispatch(changeDesktopFontWeight(weight));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileFontWeight(weight));
                }
              }}
              handleFontSizeChanged={(value) => {
                dispatch(changeDesktopFontSize(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileFontSize(value));
                }
              }}
              handleUnderlineChanged={(value) => {
                dispatch(changeDesktopUnderlineStatus(value === 'true'));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileUnderlineStatus(value === 'true'));
                }
              }}
              handleOnlyCapitalLettersChanged={(value) => {
                dispatch(
                  changeDesktopOnlyCapitalLettersStatus(value === 'true')
                );
                if (desktopAndMobileSimilar) {
                  dispatch(
                    changeMobileOnlyCapitalLettersStatus(value === 'true')
                  );
                }
              }}
              handleJustifyContent={(value) => {
                dispatch(changeDesktopJustifyContent(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileJustifyContent(value));
                }
              }}
              handleChangeWidth={(value) => {
                dispatch(changeDesktopWidth(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileWidth(value));
                }
              }}
              handleChangePadding={(value) => {
                dispatch(changeDesktopPadding(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobilePadding(value));
                }
              }}
              handleChangeMargin={(value) => {
                dispatch(changeDesktopMargin(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileMargin(value));
                }
              }}
              handleChangeBorderRadius={(value) => {
                dispatch(changeDesktopBorderRadius(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileBorderRadius(value));
                }
              }}
              handleChangeBorderType={(value) => {
                dispatch(changeDesktopBorderType(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileBorderType(value));
                }
              }}
              handleChangeBorderShape={(value) => {
                dispatch(changeDesktopBorderShape(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileBorderShape(value));
                }
              }}
              handleChangeBorderColor={(value) => {
                dispatch(changeDesktopBorderColor(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileBorderColor(value));
                }
              }}
              handleChangeBackgroundColor={(value) => {
                dispatch(changeDesktopBackgroundColor(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileBackgroundColor(value));
                }
              }}
              handleChangeHoverStyle={(value) => {
                dispatch(changeDesktopHoverStyle(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileHoverStyle(value));
                }
              }}
              handleChangeHoverBackgroundColor={(value) => {
                dispatch(changeDesktopHoverBackgroundColor(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileHoverBackgroundColor(value));
                }
              }}
              handleChangeButtonPosition={(value) => {
                dispatch(changeDesktopButtonPosition(value));
                if (desktopAndMobileSimilar) {
                  dispatch(changeMobileButtonPosition(value));
                }
              }}
              resetData={() => dispatch(resetData())}
            />
          </SettingsSection>
          <>
            {!desktopAndMobileSimilar && (
              <SettingsSection backgroundColor='#f5f5f5'>
                <FitquizAdvancedSettings
                  deviceType='mobile'
                  handleFontWeightChanged={(weight) =>
                    dispatch(changeMobileFontWeight(weight))
                  }
                  handleFontSizeChanged={(value) =>
                    dispatch(changeMobileFontSize(value))
                  }
                  handleUnderlineChanged={(value) =>
                    dispatch(changeMobileUnderlineStatus(value === 'true'))
                  }
                  handleOnlyCapitalLettersChanged={(value) =>
                    dispatch(
                      changeMobileOnlyCapitalLettersStatus(value === 'true')
                    )
                  }
                  handleJustifyContent={(value) =>
                    dispatch(changeMobileJustifyContent(value))
                  }
                  handleChangeWidth={(value) =>
                    dispatch(changeMobileWidth(value))
                  }
                  handleChangePadding={(value) =>
                    dispatch(changeMobilePadding(value))
                  }
                  handleChangeMargin={(value) =>
                    dispatch(changeMobileMargin(value))
                  }
                  handleChangeBorderRadius={(value) =>
                    dispatch(changeMobileBorderRadius(value))
                  }
                  handleChangeBorderType={(value) =>
                    dispatch(changeMobileBorderType(value))
                  }
                  handleChangeBorderShape={(value) =>
                    dispatch(changeMobileBorderShape(value))
                  }
                  handleChangeBorderColor={(value) =>
                    dispatch(changeMobileBorderColor(value))
                  }
                  handleChangeBackgroundColor={(value) =>
                    dispatch(changeMobileBackgroundColor(value))
                  }
                  handleChangeHoverStyle={(value) => {
                    dispatch(changeMobileHoverStyle(value));
                  }}
                  handleChangeHoverBackgroundColor={(value) => {
                    dispatch(changeMobileHoverBackgroundColor(value));
                  }}
                  handleChangeButtonPosition={(value) => {
                      dispatch(changeMobileButtonPosition(value));
                  }}
                  resetData={() => dispatch(resetData())}
                />
              </SettingsSection>
            )}
          </>
          <SettingsSection backgroundColor='transparent'>
            <ButtonContainer>
              <s-button onClick={submitForm} variant='primary'>
                Submit
              </s-button>
            </ButtonContainer>
          </SettingsSection>
        </Container>
      )}
    </>
  );
};

export default FitQuizSettings;

const Container = styled.div`
  background: #fafafa;
  border-radius: 12px;
  box-shadow: 0 0 10px #e6e6e6;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  background-color: #fafafa;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
`;

const SettingsSection = styled.div<{ backgroundColor: string }>`
  width: 100%;
  background: ${(p) => p.backgroundColor};
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 20px;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  padding: 0 24px 12px 24px;
`;
